import { DELETE_TENANT_WORD, type Tenant } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { APP_NAME } from '../lib/app-name';
import { api, errorMessage } from '../lib/api';
import { navigate } from '../lib/router';
import { Badge, Button, Card, ErrorText, Field } from '../ui';
import { useTenants } from './TopBar';

/**
 * L'ingresso della Fase 0 (ADR 0201): la partita IVA, e sotto le aziende già aggiunte. Vale sia per chi non ha ancora
 * un'azienda sia da Impostazioni › Aziende. Con «Aggiungi» si va alla preparazione: visura, lettura, gestionale.
 */
export function TenantsPage({ riparti = false }: { riparti?: boolean }) {
  const queryClient = useQueryClient();
  const tenants = useTenants();
  const [vat, setVat] = useState('');
  const [deleting, setDeleting] = useState<Tenant | null>(null);
  const create = useMutation({
    mutationFn: () => api<Tenant>('/tenants', { body: { vat_number: vat.replace(/[\s.-]/g, '').toUpperCase() } }),
    onSuccess: (t) => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/t/${t.id}/preparazione`);
    },
  });
  const provision = useMutation({
    mutationFn: (id: string) => api<Tenant>(`/tenants/${id}/provision`, { method: 'POST' }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['tenants'] }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (vat.trim()) create.mutate();
  };
  const list = tenants.data ?? [];
  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 px-4 py-10">
      <Card className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">{riparti ? 'Riparti dalla partita IVA giusta' : 'Aggiungi la tua azienda'}</h1>
          <p className="mt-1 text-sm text-ink/60">
            Dalla partita IVA {APP_NAME} trova l’impresa nel Registro Imprese, legge la visura camerale e prepara il gestionale su misura per le sue attività.
          </p>
        </div>
        <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
          <div className="min-w-[14rem] flex-1">
            <Field id="piva" label="Partita IVA" inputMode="numeric" placeholder="01234567897" value={vat} onChange={(e) => setVat(e.target.value)} autoComplete="off" autoFocus required />
          </div>
          <Button type="submit" disabled={create.isPending || !vat.trim()}>
            {create.isPending ? 'Aggiungo…' : 'Aggiungi'}
          </Button>
        </form>
        <ErrorText>{create.error ? errorMessage(create.error) : ''}</ErrorText>
      </Card>

      {list.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/50">Le tue aziende</h2>
          <ul className="divide-y divide-black/[0.06] rounded-xl border border-black/[0.07] bg-white">
            {list.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink">{t.name}</p>
                  {t.vat_number && <p className="text-xs text-ink/50">P.IVA {t.vat_number}</p>}
                </div>
                {t.status === 'active' && (
                  <Button variant="ghost" onClick={() => navigate(`/t/${t.id}`)}>
                    Apri
                  </Button>
                )}
                {t.status === 'provisioning' && <Badge tone="blue">In preparazione</Badge>}
                {t.status === 'failed' && (
                  <>
                    <Badge tone="red">Non si è aperta</Badge>
                    {t.role === 'owner' && (
                      <Button variant="ghost" disabled={provision.isPending} onClick={() => provision.mutate(t.id)}>
                        Riprova
                      </Button>
                    )}
                  </>
                )}
                {t.role === 'owner' && (
                  <button type="button" className="text-sm text-red-700 hover:underline" onClick={() => setDeleting(t)}>
                    Elimina
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {deleting && (
        <DeleteTenantDialog
          tenant={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
    </div>
  );
}

/** Conferma dell'eliminazione definitiva: avviso di irreversibilità e parola da scrivere. */
function DeleteTenantDialog({
  tenant,
  onClose,
  onDeleted,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [word, setWord] = useState('');
  const remove = useMutation({
    mutationFn: () => api<void>(`/tenants/${tenant.id}/delete`, { body: { confirm_word: word } }),
    onSuccess: onDeleted,
  });
  const close = () => {
    if (!remove.isPending) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={close}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-tenant-title"
        className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          remove.mutate();
        }}
      >
        <h2 id="delete-tenant-title" className="text-lg font-semibold text-red-800">
          Eliminare «{tenant.name}»?
        </h2>
        <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <p className="font-semibold">Questa azione è irreversibile.</p>
          <p>
            L’azienda verrà eliminata per sempre con il suo database, i documenti caricati e tutto
            ciò che contiene: processi, persone, archivio, collegamenti. Non potrà essere
            recuperata.
          </p>
        </div>
        <Field
          label={`Per confermare scrivi ${DELETE_TENANT_WORD}`}
          value={word}
          onChange={(e) => setWord(e.target.value)}
          autoFocus
          autoComplete="off"
          required
        />
        <ErrorText>{remove.error ? errorMessage(remove.error) : ''}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close} disabled={remove.isPending}>
            Annulla
          </Button>
          <Button
            type="submit"
            className="bg-red-700! hover:bg-red-800! disabled:bg-red-700/40!"
            disabled={remove.isPending || word.trim().toUpperCase() !== DELETE_TENANT_WORD}
          >
            {remove.isPending ? 'Eliminazione…' : 'Elimina per sempre'}
          </Button>
        </div>
      </form>
    </div>
  );
}
